import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { updateOpening } from '@/api-clients/update-opening';
import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { findCachedOpening, writeSavedOpening } from './cached-opening';

interface UseUpdateOpeningDescriptionOptions {
  onSuccess?: (openingExternalId: string) => void;
}

export function useUpdateOpeningDescription(
  options?: UseUpdateOpeningDescriptionOptions,
) {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const queryClient = useQueryClient();
  const { hookForm, recordConfirmedDetailSave, getFlowGeneration } =
    useJobPostingContext();
  const { getValues } = hookForm;

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: updateOpening,
    // Read before the request goes out: a refresh that lands while the save is
    // in flight would replace the cache with the saved values. The context
    // keeps the first baseline per opening for the flow.
    onMutate: ({ openingExternalId }) => {
      const cachedOpening = findCachedOpening(queryClient, openingExternalId);
      return {
        flowGeneration: getFlowGeneration(),
        baseline: {
          job_description: cachedOpening?.job_description,
          state_code: cachedOpening?.state_code,
        },
      };
    },
    onSuccess: (_, { openingExternalId, requestBody }, context) => {
      writeSavedOpening(queryClient, openingExternalId, requestBody);
      recordConfirmedDetailSave(
        openingExternalId,
        {
          description: requestBody.description,
          stateCode: requestBody.state_code,
          baseline: context?.baseline ?? {},
        },
        context?.flowGeneration,
      );

      // Find the opening title from the form data
      const jobDetails = getValues('jobDetails');
      const openingTitle = jobDetails?.[openingExternalId]?.title || 'Opening';

      notifySuccess({
        message: intl.formatMessage(
          {
            defaultMessage: '{openingName} - updated successfully',
            description: 'Success message for updating the opening description',
          },
          { openingName: openingTitle },
        ),
      });

      options?.onSuccess?.(openingExternalId);
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Update failed. Please try again.',
          description: 'Error message for updating the opening description',
        }),
      });
    },
  });

  return {
    updateOpeningDescription: mutate,
    updateOpeningDescriptionAsync: mutateAsync,
    isUpdatingOpening: isPending,
  };
}
